import React, { useState, useEffect } from 'react';
import { Share, ChevronDown, HelpCircle } from 'lucide-react';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 2
  }).format(amount);
};

const formatPercentage = (value) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'percent',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value / 100);
};

const BrokerageCalculator = () => {
  // State for basic inputs
  const [tradeValue, setTradeValue] = useState(100000);
  const [brokerType, setBrokerType] = useState('discount');
  const [segment, setSegment] = useState('equity-delivery');
  const [quantity, setQuantity] = useState(100);
  
  // State for additional inputs
  const [exchange, setExchange] = useState('nse');
  
  // State for advanced settings
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [customBrokerage, setCustomBrokerage] = useState(0.05);
  const [customSTT, setCustomSTT] = useState(0.1);
  const [sebiCharges, setSebiCharges] = useState(0.00005);
  const [exchangeCharges, setExchangeCharges] = useState(0.0000325);
  const [stampDuty, setStampDuty] = useState(0.00015);
  const [gst, setGst] = useState(18);
  
  // State for share toast
  const [showShareToast, setShowShareToast] = useState(false);
  
  // State for tooltips
  const [showToolTip, setShowToolTip] = useState('');
  
  // State for calculation results
  const [result, setResult] = useState({
    brokerage: 0,
    stt: 0,
    exchangeCharges: 0,
    sebiFees: 0,
    stampDuty: 0,
    gstAmount: 0,
    totalCost: 0,
    netAmount: 0,
    costPercentage: 0,
    breakEvenPrice: 0
  });

  // Calculate brokerage and other costs
  const calculateBrokerageCosts = () => {
    let brokerage = 0;
    let sttRate = customSTT / 100;
    let exchangeChargesRate = customSTT ? exchangeCharges : 0.0000325;
    let sebiFeesRate = customSTT ? sebiCharges : 0.00005;
    let stampDutyRate = customSTT ? stampDuty : 0.00015;
    
    // Set standard rates based on segment
    if (!customSTT) {
      if (segment === 'equity-delivery') {
        sttRate = 0.1 / 100; // 0.1% on buy and sell side
      } else if (segment === 'equity-intraday') {
        sttRate = 0.025 / 100; // 0.025% on sell side only
      } else if (segment === 'futures') {
        sttRate = 0.01 / 100; // 0.01% on sell side
      } else if (segment === 'options') {
        sttRate = 0.05 / 100; // 0.05% on sell side of premium
      }
    }
    
    // Calculate brokerage based on broker type
    if (brokerType === 'discount') {
      if (segment === 'equity-delivery') {
        brokerage = 0; // Most discount brokers offer zero brokerage for delivery
      } else if (segment === 'equity-intraday') {
        brokerage = Math.min(20, 0.03 / 100 * tradeValue); // 0.03% or max Rs.20 per executed order
      } else if (segment === 'futures') {
        brokerage = Math.min(20, 0.03 / 100 * tradeValue); // 0.03% or max Rs.20 per executed order
      } else if (segment === 'options') {
        brokerage = Math.min(20, 0.03 / 100 * tradeValue); // 0.03% or max Rs.20 per executed order
      }
    } else if (brokerType === 'full-service') {
      if (segment === 'equity-delivery') {
        brokerage = 0.5 / 100 * tradeValue; // 0.5% typical for full-service brokers
      } else if (segment === 'equity-intraday') {
        brokerage = 0.05 / 100 * tradeValue; // 0.05% for intraday
      } else if (segment === 'futures') {
        brokerage = 0.05 / 100 * tradeValue; // 0.05% for futures
      } else if (segment === 'options') {
        brokerage = 0.05 / 100 * tradeValue; // 0.05% for options
      }
    } else if (brokerType === 'custom') {
      brokerage = (customBrokerage / 100) * tradeValue; // User-defined brokerage
    }
    
    // Calculate other charges
    const stt = sttRate * tradeValue;
    const exchangeCharge = exchangeChargesRate * tradeValue;
    const sebiFee = sebiFeesRate * tradeValue;
    const stamp = stampDutyRate * tradeValue;
    
    // Calculate GST (18% on brokerage and exchange charges)
    const gstAmount = (gst / 100) * (brokerage + exchangeCharge);
    
    // Calculate total cost
    const totalCost = brokerage + stt + exchangeCharge + sebiFee + stamp + gstAmount;
    
    // Calculate net amount
    let netAmount = 0;
    if (segment === 'equity-delivery' || segment === 'equity-intraday') {
      netAmount = tradeValue - totalCost;
    } else {
      netAmount = tradeValue - totalCost;
    }
    
    // Calculate cost as percentage of trade value
    const costPercentage = (totalCost / tradeValue) * 100;
    
    // Calculate breakeven price for long positions
    // For a profitable trade, selling price must cover buying price + total costs on both sides
    let breakEvenPrice = 0;
    const pricePerShare = tradeValue / quantity;
    
    if (segment === 'equity-delivery') {
      // For delivery, we need to account for costs on both buy and sell sides
      breakEvenPrice = pricePerShare * (1 + (costPercentage / 100) * 2);
    } else if (segment === 'equity-intraday') {
      // For intraday, costs are lower and we're making round-trip calculation
      breakEvenPrice = pricePerShare * (1 + (costPercentage / 100) * 2);
    } else {
      // For derivatives, similar to intraday
      breakEvenPrice = pricePerShare * (1 + (costPercentage / 100) * 2);
    }
    
    return {
      brokerage,
      stt,
      exchangeCharges: exchangeCharge,
      sebiFees: sebiFee,
      stampDuty: stamp,
      gstAmount,
      totalCost,
      netAmount,
      costPercentage,
      breakEvenPrice
    };
  };

  // Recalculate whenever inputs change
  useEffect(() => {
    setResult(calculateBrokerageCosts());
  }, [
    tradeValue, 
    brokerType,
    segment,
    quantity,
    exchange,
    customBrokerage,
    customSTT,
    sebiCharges,
    exchangeCharges,
    stampDuty,
    gst
  ]);

  // Handle share button click
  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setShowShareToast(true);
      setTimeout(() => setShowShareToast(false), 3000);
    } catch (err) {
      console.error('Failed to copy URL:', err);
    }
  };

  return (
    <div className="calculator-container pt-24">
      <div className="calculator-header text-center mb-8">
        <h1 className="text-2xl font-semibold text-[#113262] mb-2">Brokerage Cost Calculator</h1>
        <h2 className="text-lg text-gray-600">Estimate the total cost of your stock market trades</h2>
      </div>

      <div className="max-w-5xl mx-auto p-4">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Input Sections */}
          <div className="lg:col-span-2 space-y-8">
            {/* Trade Details */}
            <div className="bg-white rounded-lg shadow p-4">
              <h2 className="text-lg font-bold text-gray-900 mb-4">Trade Details</h2>
              <div className="grid grid-cols-2 gap-6">
                <div className="col-span-2">
                  <label className="block font-medium text-sm mb-1.5 flex items-center">
                    Trade Value
                    <div className="relative ml-1">
                      <HelpCircle 
                        size={14} 
                        className="text-gray-400 cursor-pointer"
                        onMouseEnter={() => setShowToolTip('tradeValue')}
                        onMouseLeave={() => setShowToolTip('')}
                      />
                      {showToolTip === 'tradeValue' && (
                        <div className="absolute z-10 left-6 -top-2 w-60 p-2 bg-gray-800 text-white text-xs rounded shadow-lg">
                          Total value of the trade (Price × Quantity)
                        </div>
                      )}
                    </div>
                  </label>
                  <input
                    type="number"
                    value={tradeValue}
                    onChange={(e) => setTradeValue(Number(e.target.value))}
                    className="w-full p-1.5 border rounded text-sm"
                  />
                  <input
                    type="range"
                    min={1000}
                    max={1000000}
                    step={1000}
                    value={tradeValue}
                    onChange={(e) => setTradeValue(Number(e.target.value))}
                    className="w-full mt-2"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>₹1K</span>
                    <span>₹10L</span>
                  </div>
                </div>
                
                <div>
                  <label className="block font-medium text-sm mb-1.5">Quantity</label>
                  <input
                    type="number"
                    value={quantity}
                    onChange={(e) => setQuantity(Number(e.target.value))}
                    className="w-full p-1.5 border rounded text-sm"
                  />
                  <input
                    type="range"
                    min={1}
                    max={1000}
                    step={1}
                    value={quantity}
                    onChange={(e) => setQuantity(Number(e.target.value))}
                    className="w-full mt-2"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>1</span>
                    <span>1000</span>
                  </div>
                </div>
                
                <div>
                  <label className="block font-medium text-sm mb-1.5">Exchange</label>
                  <select
                    value={exchange}
                    onChange={(e) => setExchange(e.target.value)}
                    className="w-full p-1.5 border rounded text-sm"
                  >
                    <option value="nse">NSE</option>
                    <option value="bse">BSE</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Broker Type */}
            <div className="bg-white rounded-lg shadow p-4">
              <h2 className="text-lg font-bold text-gray-900 mb-4">Broker Type</h2>
              <div className="grid grid-cols-1 gap-6">
                <div className="col-span-1">
                  <label className="block font-medium text-sm mb-3 flex items-center">
                    Select Broker Type
                    <div className="relative ml-1">
                      <HelpCircle 
                        size={14} 
                        className="text-gray-400 cursor-pointer"
                        onMouseEnter={() => setShowToolTip('brokerType')}
                        onMouseLeave={() => setShowToolTip('')}
                      />
                      {showToolTip === 'brokerType' && (
                        <div className="absolute z-10 left-6 -top-2 w-72 p-2 bg-gray-800 text-white text-xs rounded shadow-lg">
                          Discount brokers typically charge lower fees than full-service brokers. Select custom to enter your own rates.
                        </div>
                      )}
                    </div>
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    <div 
                      className={`border p-3 rounded-lg cursor-pointer transition-colors text-center ${
                        brokerType === 'discount' ? 'bg-blue-50 border-blue-500' : 'hover:bg-gray-50'
                      }`}
                      onClick={() => setBrokerType('discount')}
                    >
                      <p className="font-medium">Discount Broker</p>
                      <p className="text-xs text-gray-500 mt-1">Lower fees, basic services</p>
                    </div>
                    <div 
                      className={`border p-3 rounded-lg cursor-pointer transition-colors text-center ${
                        brokerType === 'full-service' ? 'bg-blue-50 border-blue-500' : 'hover:bg-gray-50'
                      }`}
                      onClick={() => setBrokerType('full-service')}
                    >
                      <p className="font-medium">Full-Service Broker</p>
                      <p className="text-xs text-gray-500 mt-1">Higher fees, more services</p>
                    </div>
                    <div 
                      className={`border p-3 rounded-lg cursor-pointer transition-colors text-center ${
                        brokerType === 'custom' ? 'bg-blue-50 border-blue-500' : 'hover:bg-gray-50'
                      }`}
                      onClick={() => setBrokerType('custom')}
                    >
                      <p className="font-medium">Custom</p>
                      <p className="text-xs text-gray-500 mt-1">Enter your own rates</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Segment Type */}
            <div className="bg-white rounded-lg shadow p-4">
              <h2 className="text-lg font-bold text-gray-900 mb-4">Segment</h2>
              <div className="grid grid-cols-1 gap-6">
                <div className="col-span-1">
                  <label className="block font-medium text-sm mb-3 flex items-center">
                    Select Trading Segment
                    <div className="relative ml-1">
                      <HelpCircle 
                        size={14} 
                        className="text-gray-400 cursor-pointer"
                        onMouseEnter={() => setShowToolTip('segment')}
                        onMouseLeave={() => setShowToolTip('')}
                      />
                      {showToolTip === 'segment' && (
                        <div className="absolute z-10 left-6 -top-2 w-72 p-2 bg-gray-800 text-white text-xs rounded shadow-lg">
                          Different segments have different brokerage structures and tax implications
                        </div>
                      )}
                    </div>
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <div 
                      className={`border p-3 rounded-lg cursor-pointer transition-colors text-center ${
                        segment === 'equity-delivery' ? 'bg-blue-50 border-blue-500' : 'hover:bg-gray-50'
                      }`}
                      onClick={() => setSegment('equity-delivery')}
                    >
                      <p className="font-medium">Equity Delivery</p>
                      <p className="text-xs text-gray-500 mt-1">T+2 settlement, higher STT</p>
                    </div>
                    <div 
                      className={`border p-3 rounded-lg cursor-pointer transition-colors text-center ${
                        segment === 'equity-intraday' ? 'bg-blue-50 border-blue-500' : 'hover:bg-gray-50'
                      }`}
                      onClick={() => setSegment('equity-intraday')}
                    >
                      <p className="font-medium">Equity Intraday</p>
                      <p className="text-xs text-gray-500 mt-1">Same-day settlement, lower STT</p>
                    </div>
                    <div 
                      className={`border p-3 rounded-lg cursor-pointer transition-colors text-center ${
                        segment === 'futures' ? 'bg-blue-50 border-blue-500' : 'hover:bg-gray-50'
                      }`}
                      onClick={() => setSegment('futures')}
                    >
                      <p className="font-medium">Futures</p>
                      <p className="text-xs text-gray-500 mt-1">F&O segment, leverage</p>
                    </div>
                    <div 
                      className={`border p-3 rounded-lg cursor-pointer transition-colors text-center ${
                        segment === 'options' ? 'bg-blue-50 border-blue-500' : 'hover:bg-gray-50'
                      }`}
                      onClick={() => setSegment('options')}
                    >
                      <p className="font-medium">Options</p>
                      <p className="text-xs text-gray-500 mt-1">F&O segment, limited risk</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Advanced Settings */}
            <div className="bg-white rounded-lg shadow">
              <button
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="w-full p-4 flex justify-between items-center hover:bg-gray-50"
              >
                <h2 className="text-lg font-bold text-gray-900">Advanced Settings</h2>
                <ChevronDown 
                  className={`transform transition-transform ${showAdvanced ? 'rotate-180' : ''}`}
                  size={20}
                />
              </button>
              
              {showAdvanced && (
                <div className="p-4 border-t">
                  <div className="grid grid-cols-2 gap-6">
                    {brokerType === 'custom' && (
                      <div>
                        <label className="block font-medium text-sm mb-1.5">Brokerage (%)</label>
                        <input
                          type="number"
                          value={customBrokerage}
                          onChange={(e) => setCustomBrokerage(Number(e.target.value))}
                          step="0.01"
                          className="w-full p-1.5 border rounded text-sm"
                        />
                        <input
                          type="range"
                          min={0}
                          max={1}
                          step={0.01}
                          value={customBrokerage}
                          onChange={(e) => setCustomBrokerage(Number(e.target.value))}
                          className="w-full mt-2"
                        />
                        <div className="flex justify-between text-xs text-gray-500 mt-1">
                          <span>0%</span>
                          <span>1%</span>
                        </div>
                      </div>
                    )}

                    <div>
                      <label className="block font-medium text-sm mb-1.5">STT (%)</label>
                      <input
                        type="number"
                        value={customSTT}
                        onChange={(e) => setCustomSTT(Number(e.target.value))}
                        step="0.01"
                        className="w-full p-1.5 border rounded text-sm"
                      />
                      <input
                        type="range"
                        min={0}
                        max={0.2}
                        step={0.01}
                        value={customSTT}
                        onChange={(e) => setCustomSTT(Number(e.target.value))}
                        className="w-full mt-2"
                      />
                      <div className="flex justify-between text-xs text-gray-500 mt-1">
                        <span>0%</span>
                        <span>0.2%</span>
                      </div>
                    </div>

                    <div>
                      <label className="block font-medium text-sm mb-1.5">Exchange Charges (%)</label>
                      <input
                        type="number"
                        value={exchangeCharges * 100}
                        onChange={(e) => setExchangeCharges(Number(e.target.value) / 100)}
                        step="0.001"
                        className="w-full p-1.5 border rounded text-sm"
                      />
                      <input
                        type="range"
                        min={0}
                        max={0.01}
                        step={0.0001}
                        value={exchangeCharges}
                        onChange={(e) => setExchangeCharges(Number(e.target.value))}
                        className="w-full mt-2"
                      />
                      <div className="flex justify-between text-xs text-gray-500 mt-1">
                        <span>0%</span>
                        <span>0.01%</span>
                      </div>
                    </div>

                    <div>
                      <label className="block font-medium text-sm mb-1.5">SEBI Charges (%)</label>
                      <input
                        type="number"
                        value={sebiCharges * 100}
                        onChange={(e) => setSebiCharges(Number(e.target.value) / 100)}
                        step="0.001"
                        className="w-full p-1.5 border rounded text-sm"
                      />
                      <input
                        type="range"
                        min={0}
                        max={0.01}
                        step={0.0001}
                        value={sebiCharges}
                        onChange={(e) => setSebiCharges(Number(e.target.value))}
                        className="w-full mt-2"
                      />
                      <div className="flex justify-between text-xs text-gray-500 mt-1">
                        <span>0%</span>
                        <span>0.01%</span>
                      </div>
                    </div>

                    <div>
                      <label className="block font-medium text-sm mb-1.5">Stamp Duty (%)</label>
                      <input
                        type="number"
                        value={stampDuty * 100}
                        onChange={(e) => setStampDuty(Number(e.target.value) / 100)}
                        step="0.001"
                        className="w-full p-1.5 border rounded text-sm"
                      />
                      <input
                        type="range"
                        min={0}
                        max={0.01}
                        step={0.0001}
                        value={stampDuty}
                        onChange={(e) => setStampDuty(Number(e.target.value))}
                        className="w-full mt-2"
                      />
                      <div className="flex justify-between text-xs text-gray-500 mt-1">
                        <span>0%</span>
                        <span>0.01%</span>
                      </div>
                    </div>

                    <div>
                      <label className="block font-medium text-sm mb-1.5">GST (%)</label>
                      <input
                        type="number"
                        value={gst}
                        onChange={(e) => setGst(Number(e.target.value))}
                        step="1"
                        className="w-full p-1.5 border rounded text-sm"
                      />
                      <input
                        type="range"
                        min={0}
                        max={28}
                        step={1}
                        value={gst}
                        onChange={(e) => setGst(Number(e.target.value))}
                        className="w-full mt-2"
                      />
                      <div className="flex justify-between text-xs text-gray-500 mt-1">
                        <span>0%</span>
                        <span>28%</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Results Section */}
          <div className="bg-[#113262] text-white rounded-lg h-[500px] sticky top-6">
            <div className="p-4 border-b border-white/20">
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-bold">Brokerage & Costs</h3>
                <button onClick={handleShare} className="p-1 hover:bg-blue-700 rounded">
                  <Share size={18} />
                </button>
              </div>
            </div>

            <div className="p-4 flex flex-col h-[calc(100%-68px)] justify-between">
              <div className="space-y-4">
                <div className="text-center">
                  <div className="text-2xl font-bold mb-1">
                    {formatCurrency(result.totalCost)}
                  </div>
                  <div className="text-sm text-gray-300">Total Transaction Cost</div>
                </div>

                <div className="space-y-3">
                  <div className="flex justify-between items-center py-2 border-t border-white/20">
                    <span className="text-sm">Brokerage</span>
                    <span className="font-bold">{formatCurrency(result.brokerage)}</span>
                  </div>
                  
                  <div className="flex justify-between items-center py-2 border-t border-white/20">
                    <span className="text-sm">STT</span>
                    <span className="font-bold">{formatCurrency(result.stt)}</span>
                  </div>

                  <div className="flex justify-between items-center py-2 border-t border-white/20">
                    <span className="text-sm">Exchange Charges</span>
                    <span className="font-bold">{formatCurrency(result.exchangeCharges)}</span>
                  </div>

                  <div className="flex justify-between items-center py-2 border-t border-white/20">
                    <span className="text-sm">SEBI Fees</span>
                    <span className="font-bold">{formatCurrency(result.sebiFees)}</span>
                  </div>

                  <div className="flex justify-between items-center py-2 border-t border-white/20">
                    <span className="text-sm">Stamp Duty</span>
                    <span className="font-bold">{formatCurrency(result.stampDuty)}</span>
                  </div>

                  <div className="flex justify-between items-center py-2 border-t border-white/20">
                    <span className="text-sm">GST</span>
                    <span className="font-bold">{formatCurrency(result.gstAmount)}</span>
                  </div>

                  <div className="flex justify-between items-center py-2 border-t border-white/20">
                    <span className="text-sm">Cost as % of Trade</span>
                    <span className="font-bold">{result.costPercentage.toFixed(3)}%</span>
                  </div>

                  <div className="flex justify-between items-center py-2 border-t border-white/20">
                    <span className="text-sm">Breakeven Price Per Unit</span>
                    <span className="font-bold">{formatCurrency(result.breakEvenPrice)}</span>
                  </div>
                </div>
              </div>

              <button className="w-full bg-orange-400 text-white py-2 rounded-lg mt-4 hover:bg-orange-500 transition-colors text-sm">
                Open Trading Account →
              </button>
            </div>
          </div>

          {showShareToast && (
            <div className="fixed top-4 right-4 bg-green-600 text-white px-6 py-3 rounded shadow-lg">
              Link copied!
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BrokerageCalculator;